import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import MediaLibrary from "@/components/feature/MediaLibrary";
import type { MediaItem } from "@/services/mediaService";
import { adminCard } from "@/pages/portal/admin/config/ui";

/** Distinct role-scale fills for each folder segment in the storage bar. */
const FOLDER_COLORS: Record<string, string> = {
  listings: "bg-primary-500",
  gallery: "bg-accent-500",
  ads: "bg-secondary-500",
  logos: "bg-primary-300",
  avatars: "bg-accent-300",
  branding: "bg-secondary-300",
  media: "bg-foreground-400",
  fonts: "bg-primary-700",
};

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

/**
 * Admin — Media Library.
 *
 * Browses and manages the files stored in the site's uploads folder on the
 * server (UPLOAD_DIR — e.g. /home/coastal/public_html/uploads). From here an
 * admin can search, filter by folder, upload new images, copy a file's public
 * URL and delete files that are no longer needed.
 *
 * The library grid itself is the shared Media Handler block, so the admin sees
 * exactly the same experience business owners get in their portal.
 */
export default function MediaSection() {
  const { accessToken } = useAuth();
  const [items, setItems] = useState<MediaItem[]>([]);

  /** Per-folder + overall storage totals for the summary bar. */
  const storage = useMemo(() => {
    const byFolder = new Map<string, { size: number; count: number }>();
    let totalSize = 0;
    for (const item of items) {
      totalSize += item.size;
      const current = byFolder.get(item.folder) ?? { size: 0, count: 0 };
      current.size += item.size;
      current.count += 1;
      byFolder.set(item.folder, current);
    }
    const rows = Array.from(byFolder.entries())
      .map(([name, value]) => ({ name, ...value }))
      .sort((a, b) => b.size - a.size);
    return { totalSize, totalCount: items.length, rows };
  }, [items]);

  return (
    <div>
      {items.length > 0 && (
        <div className={`${adminCard} mb-5 p-4`}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-hard-drive-3-line text-sm" />
              </span>
              <h3 className="text-sm font-semibold text-foreground-900">Storage usage</h3>
            </div>
            <p className="text-xs text-foreground-500">
              <strong className="text-foreground-800">{formatBytes(storage.totalSize)}</strong> across{" "}
              {storage.totalCount} file{storage.totalCount === 1 ? "" : "s"}
            </p>
          </div>

          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-background-200">
            {storage.rows.map((row) => (
              <span
                key={row.name}
                className={`${FOLDER_COLORS[row.name] ?? "bg-foreground-300"} h-full`}
                style={{ width: `${storage.totalSize ? (row.size / storage.totalSize) * 100 : 0}%` }}
                title={`${row.name}: ${formatBytes(row.size)}`}
              />
            ))}
          </div>

          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
            {storage.rows.map((row) => (
              <div key={row.name} className="flex items-center gap-2 text-xs">
                <span className={`h-2.5 w-2.5 rounded-sm ${FOLDER_COLORS[row.name] ?? "bg-foreground-300"}`} />
                <span className="font-medium capitalize text-foreground-700">{row.name}</span>
                <span className="text-foreground-400">
                  {row.count} file{row.count === 1 ? "" : "s"} · {formatBytes(row.size)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-xs text-secondary-900">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
          <i className="ri-hard-drive-3-line text-base" />
        </span>
        <p>
          These files live in the site&apos;s uploads folder on the server and are served directly at
          their public URL. Deleting a file removes it from the server — any page still pointing at it
          will show a broken image.
        </p>
      </div>

      <div className={`${adminCard} p-4 md:p-5`}>
        <MediaLibrary
          token={accessToken}
          scope="admin"
          mode="manage"
          canUpload
          canDelete
          onDataChange={setItems}
        />
      </div>
    </div>
  );
}